// Source taxonomy filter. SOURCE OF TRUTH: the sibling torah-map app
// (C:\Users\iehab\Desktop\CODE PROJECTS\torah-map\scripts\build-torah-map.js -
// EXCLUDED_NODE_IDS, EXCLUDED_BREADCRUMB_KEYWORDS, CATEGORY_KEYWORD_MAP). Ported
// here so TorahTrail surfaces the SAME curated set of sources torah-map approved.
//
// Design = ALLOWLIST, not blocklist. A Sefaria link is shown only if its category
// is in ACCEPTED_ORDER AND no exclude keyword matches. Anything unrecognized is
// dropped, so the filter stays correct even if the real ignore set is larger than
// the list below ("ignore might be bigger than what it is"). The filter only
// SUBTRACTS - it never invents a ref, so accuracy-lock is preserved.

import type { SourceLink } from "./sefaria.ts";

// Accepted top-level Sefaria link categories, in display order. These mirror the
// accepted branches of the torah-map tree (Tanakh + its commentary, Mishnah,
// Talmud, Tosefta, Halakhah, Midrash, Kabbalah, Chasidut, Musar, Machshavah).
// Commentary is first so Rashi and the meforshim lead (matches prior behaviour).
export const ACCEPTED_ORDER: readonly string[] = [
  "Commentary",
  "Targum",
  "Midrash",
  "Mishnah",
  "Talmud",
  "Tosefta",
  "Halakhah",
  "Responsa",
  "Kabbalah",
  "Chasidut",
  "Musar",
  "Jewish Thought",
  "Philosophy",
  "Tanakh",
];

const ACCEPTED = new Set(ACCEPTED_ORDER.map((c) => c.toLowerCase()));

// Family drops, split by WHAT they match so a generic word inside a legitimate
// title cannot cause collateral. (review fix tax-3: the old single haystack
// substring-matched category+commentator+ref, which dropped "Siddur Rashi" - a
// real Rishonic halacha work - on the word "siddur".)
// CATEGORY_EXCLUDE matches ONLY the Sefaria top-level category, mirroring
// torah-map's breadcrumb-scoped intent (these are corpus/category names, not
// words that appear inside classical titles). EDITION_EXCLUDE matches the
// commentator + ref, for excluded editions/authors that live inside an otherwise
// accepted category (Tosefta Lieberman edition, Abulafia kabbalah works).
const CATEGORY_EXCLUDE: readonly string[] = [
  "liturgy", "reference", "second temple", "philo", "josephus", "dictionary",
  "grammar", "encyclopedic", "apocrypha", "modern commentary", "modern works",
  "modern text", "minor tractates", "quoting commentary", "other kabbalah",
  "other liturgy", "piyut", "siddur", "haggadah", "high holidays",
];
const EDITION_EXCLUDE: readonly string[] = ["lieberman", "abulafia"];

// Non-traditional / academic works that Sefaria files under ACCEPTED top-level
// categories (e.g. "Tanakh" > "Modern Commentary on Tanakh", or "Jewish Thought"
// > "Modern") and so slip past the category allowlist. This is a TRADITIONAL
// Orthodox source-navigator: per the curator's rule we KEEP classical sources AND
// modern-ORTHODOX voices (Rabbi Sacks, Rav Kook, S.R. Hirsch, Rav Uziel, Rav
// Amiel, David Zvi Hoffmann, Erica Brown, Torah of the Mothers ...), and DROP
// Reform / Conservative / Reconstructionist / academic-secular works. Matched as
// distinctive norm()-substrings of the work title (norm() strips the apostrophe
// and colon, so "Women's"/"Women`s" both match). Purely subtractive - it never
// admits anything, only hides. Curated 2026-06-17; extend as new ones surface.
// NOTE: Umberto Cassuto was considered for this list but is KEPT by curator
// decision (2026-06-17) - academic-critical method, but a noted opponent of the
// Documentary Hypothesis and cited with respect in traditional circles. Do not
// re-add "cassuto" here without checking with the curator first.
const NON_TRADITIONAL_EXCLUDE: readonly string[] = [
  "womens commentary", // The Torah; A Women's Commentary (Reform - Women of Reform Judaism / CCAR Press)
  "dirshuni", // Dirshuni I/II (non-denominational feminist "new midrash")
  "everett fox", // Everett Fox's academic Buber-Rosenzweig-school translation
  "standing again at sinai", // Standing Again at Sinai (Judith Plaskow - Reform/feminist theology)
];

// The approved Tanakh commentators: the 40 Rishonim + 55 Acharonim torah-map
// accepts (each as its title and Sefaria index-title, normalized; plus the "X"
// prefix of "X on Torah"-style titles). Generated from torah-map/public/torah-map.json.
// Sefaria tags modern/academic Chumash commentaries under the same broad
// "Commentary" category as Rashi, so a category check alone lets them through;
// this set gates "Commentary" down to the approved meforshim. The other accepted
// categories are bounded classical corpora and pass at the category level.
const ACCEPTED_COMMENTATORS = new Set<string>([
  "abarbanel", "aderet eliyahu", "aderet eliyahu rabbi yosef chaim", "ahavat yehonatan",
  "alon bakhut", "alon bakhut on lamentations", "alshich", "ateret zekeinim", "avi ezer",
  "baal haturim", "baalei brit avram", "bartenura", "bartenura on torah", "beit halevi",
  "beit halevi on torah", "bekhor shor", "chanukat hatorah", "chatam sofer",
  "chatam sofer on torah", "chida", "chizkuni", "daat zekenim", "divrei david", "em lamikra",
  "eretz chemdah", "eshkol hakofer", "eshkol hakofer on ruth", "ezra ben solomon",
  "ezra ben solomon on song of songs", "gevia kesef", "gur aryeh", "haamek davar",
  "hadar zekenim", "hadar zekenim on torah", "haktav vehakabalah", "harchev davar", "ibn ezra",
  "iggeret shmuel", "iggeret shmuel on ruth", "immanuel of rome", "immanuel of rome on esther",
  "imrei daat", "imrei daat on proverbs", "imrei yosher", "joseph ibn yahya", "keli chemdah",
  "keli chemdah on torah", "kitzur baal haturim", "kli yakar", "lechem dimah",
  "lechem dimah on eikhah", "levush haorah", "malbim", "mashmia yeshuah", "maskil ledavid",
  "mechokekei yehudah", "megalleh amukkot", "megalleh amukkot on torah", "meiri",
  "meiri on psalms", "mekhir yayin", "mekhir yayin on esther", "menot halevi",
  "menot halevi on esther", "meshech hochma", "metzudat david", "metzudat zion", "minchat ani",
  "minchat ani on torah", "minchat shai", "minei targuma", "minei targuma on torah", "mizrachi",
  "nachalat yaakov", "netinah lager", "od yosef chai derashot", "ohel yaakov",
  "ohel yaakov on torah", "ohev ger", "ohr chadash", "or hachaim", "paaneach raza",
  "pardes yosef", "rabbeinu bahya", "rabbeinu chananel", "radak", "ralbag",
  "ralbag beur hamilot", "ramban", "rashbam", "rashi", "rav hirsch", "rav hirsch on torah",
  "reggio", "reggio on torah", "riva", "riva on torah", "rosh", "rosh on torah", "saadia gaon",
  "second version of ibn ezra", "sforno", "shadal", "shelom esther", "siftei chakhamim",
  "siftei kohen", "siftei kohen on torah", "tevat gome", "tiferet yehonatan",
  "tiferet yehonatan on torah", "tikvat enosh", "tikvat enosh on job", "toledot yitzchak",
  "toledot yitzchak on torah", "torah temimah", "tur haaroch", "tzafnat paneach",
  "tzafnat paneach on torah", "tzeenah ureenah", "tzror hamor", "tzror hamor on torah",
  "yeriot shlomo", "yeriot shlomo on torah", "yesha elohim", "yesha elohim on esther",
]);

// The ONE name-normalization convention for matching Sefaria titles against
// torah-map-derived sets (era.ts imports this so the two can never drift).
export const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();

// A "Commentary" link is shown only if it is one of the approved meforshim,
// matched on the commentator name or the work title (the "X" of "X on Book ...").
function commentaryAllowed(link: SourceLink): boolean {
  if (ACCEPTED_COMMENTATORS.has(norm(link.commentator))) return true;
  const head = norm(link.ref.split(/ on /i)[0]);
  return head !== "" && ACCEPTED_COMMENTATORS.has(head);
}

// The canonical accepted category for a link, or null if it is not in the
// approved taxonomy (the allowlist gate).
export function acceptedCategory(link: SourceLink): string | null {
  const cat = link.category.trim();
  return ACCEPTED.has(cat.toLowerCase()) ? cat : null;
}

// True if this source should be shown: an accepted category, not an excluded
// category-family, not an excluded edition/author, and (for the broad Commentary
// category) an approved mefaresh.
export function acceptSourceLink(link: SourceLink): boolean {
  const cat = acceptedCategory(link);
  if (cat === null) return false;
  const catLc = cat.toLowerCase();
  if (CATEGORY_EXCLUDE.some((kw) => catLc.includes(kw))) return false;
  const authorRef = `${link.commentator} ${link.ref}`.toLowerCase();
  if (EDITION_EXCLUDE.some((kw) => authorRef.includes(kw))) return false;
  // Non-traditional / academic works (matched on the norm'd title - punctuation
  // and apostrophe stripped) are hidden even though their category is accepted.
  const workName = norm(authorRef);
  if (NON_TRADITIONAL_EXCLUDE.some((kw) => workName.includes(kw))) return false;
  if (catLc === "commentary") return commentaryAllowed(link);
  return true;
}

// Display rank for an accepted category (used to order the source groups).
const CATEGORY_RANK = new Map(ACCEPTED_ORDER.map((c, i) => [c.toLowerCase(), i]));
export function categoryRank(category: string): number {
  return CATEGORY_RANK.get(category.trim().toLowerCase()) ?? 99;
}
